/**
 * Snake-draft geometry for the board.
 *
 * The board draws teams as COLUMNS and rounds as ROWS. A team owns the same
 * column in every round -- the snake decides the ORDER teams pick in, not which
 * column they sit in. So a cell's position is (round, teamNumber) with no snake
 * handling at all, and the snake shows up in exactly two places: working out
 * which team owns a given overall pick, and the pick number the cell displays.
 */

/** The round an overall pick falls in. */
export const roundForPick = (pickNo, teams) => Math.floor((pickNo - 1) / teams) + 1;

/**
 * Which team (1-based) owns `pickNo`. This is Sleeper's `draft_slot`, rederived
 * so the board can place picks itself.
 */
export const teamForPick = (pickNo, teams) => {
  const round = roundForPick(pickNo, teams);
  const indexInRound = (pickNo - 1) % teams;
  // Even rounds run backwards: the first pick of the round belongs to the last team.
  return round % 2 === 0 ? teams - indexInRound : indexInRound + 1;
};

/** Flat index of the cell that holds `pickNo`. */
export const gridIndexForPick = (pickNo, teams) =>
  (roundForPick(pickNo, teams) - 1) * teams + (teamForPick(pickNo, teams) - 1);

/**
 * The pick's order within its round, which is what a cell shows after the dot.
 * In an even round the leftmost column picks last, so the label counts down.
 */
export const pickInRoundForCell = (index, teams) => {
  const round = Math.floor(index / teams) + 1;
  const teamNumber = (index % teams) + 1;
  return round % 2 === 0 ? teams + 1 - teamNumber : teamNumber;
};

/** The round a cell sits in. */
export const roundForCell = (index, teams) => Math.floor(index / teams) + 1;

/**
 * Order to fill a round's empty cells with best-available players: the column
 * that picks first gets the best player left.
 */
export const fillOrderForRound = (round, teams) => {
  const columns = Array.from({ length: teams }, (unused, i) => i);
  return round % 2 === 0 ? columns.reverse() : columns;
};

/**
 * A team's pick order within a round.
 *
 * Even rounds run backwards, so this mirrors the column. The mirror is its own
 * inverse -- feed it a team number and you get a pick-in-round, feed it a
 * pick-in-round and you get the team back -- which is exactly why the two were
 * easy to confuse for each other.
 */
export const pickInRoundForTeam = (round, teamNumber, teams) =>
  (round % 2 === 0 ? teams + 1 - teamNumber : teamNumber);

/** The team holding a given pick-in-round. Same mirror, read the other way. */
export const teamForPickInRound = (round, pickInRound, teams) =>
  pickInRoundForTeam(round, pickInRound, teams);

/**
 * Overall pick number, from the pick's order WITHIN its round.
 *
 * Takes a pick-in-round, never a team number: the snake has already been
 * accounted for by the time you have one. Mirroring here as well double-counts
 * it and hands back the column -- which is how the first pick of round 2 came
 * to be labelled overall #24 instead of #13.
 */
export const overallPickForRoundPick = (round, pickInRound, teams) =>
  (round - 1) * teams + pickInRound;
